import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { useSearchParams } from 'react-router-dom';
import {
  FileIcon, ClockIcon, InfoIcon, ViewIcon, PlayIcon, SearchIcon, ChevronRightIcon, ChevronDownIcon,
  AlertTriangleIcon, CheckIcon, UploadIcon, RefreshIcon, XSmallIcon, MinusIcon,
} from '@shopify/polaris-icons';
import { api } from './apiClient';
import { useAlerts } from '../alerts';
import { Ic, StatusPill, EmDash } from '../ui';
import TeamaticalIcon from './TeamaticalIcon';
import AuditPanel from './AuditPanel';

const TABS = ['single', 'csv', 'history', 'audit'];
const RECENT_ROWS = 8;
const STATS_WINDOW_DAYS = 30;
const ADMIN_PRODUCT_URL = 'https://admin.shopify.com/store/soccerpostshop/products/';

const RUN_STATE = {
  complete: ['ful', 'Complete'],
  pending_cleanup: ['hold', 'Pending cleanup'],
  failed: ['can', 'Failed'],
};
const FILTERS = [['all', 'All statuses'], ['complete', 'Complete'], ['pending_cleanup', 'Pending cleanup'], ['failed', 'Failed']];

const CHECK_ICON = { ok: CheckIcon, info: InfoIcon, warn: AlertTriangleIcon, bad: AlertTriangleIcon, error: XSmallIcon };
const STEP_ICON = { done: CheckIcon, skipped: CheckIcon, kept: AlertTriangleIcon, failed: XSmallIcon };

const CONFIRM_TEXT = {
  different_id: (item) => `Replace ${item.shop?.teamatical_id} with ${item.teamatical_id}`,
  sku_mismatch: () => 'Prepare despite the SKU mismatch',
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const idFromGid = (gid) => String(gid || '').split('/').pop();

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function teamaticalIdFromUrl(url) {
  const m = String(url || '').trim().match(/\/([A-Za-z0-9]{4,20})\/?$/);
  return m ? m[1].toUpperCase() : '';
}

function Alert({ tone = '', children }) {
  const icon = tone === 'ok' ? CheckIcon : tone === 'info' ? InfoIcon : AlertTriangleIcon;
  return <div className={`uh-alert ${tone}`}><Ic source={icon} sm /><div>{children}</div></div>;
}

// Headline state for a preview row.
function itemState(item) {
  if (item.blocked) return ['can', 'Can’t prepare'];
  const levels = item.checks.map((c) => c.level);
  if (levels.includes('bad')) return ['can', item.confirm.includes('sku_mismatch') ? 'SKU mismatch' : 'Different ID'];
  if (item.shop?.teamatical_id) return ['neu', 'Already configured'];
  if (levels.includes('warn')) return ['hold', 'Ready · review warning'];
  return ['ful', 'Ready'];
}

// Teamatical blocks its images from showing on other sites, so they come
// through our API instead.
function TeamaticalImage({ teamaticalId }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let live = true;
    setSrc(null);
    setFailed(false);
    api.teamaticalImage(teamaticalId)
      .then((blob) => { if (live) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [teamaticalId]);
  if (src) return <img alt="" src={src} />;
  return <div className="noimg">{failed ? 'Image unavailable' : 'Loading…'}</div>;
}

function SideCard({ source, image, title, rows }) {
  return (
    <div className="uh-tp-side">
      {React.isValidElement(image) ? image : image ? <img alt="" src={image} loading="lazy" /> : <div className="noimg">No image</div>}
      <div style={{ minWidth: 0 }}>
        <div className="src">{source}</div>
        <div className="pt">{title}</div>
        <div className="uh-kv">
          {rows.map(([k, v]) => <React.Fragment key={k}><span>{k}</span><b>{v}</b></React.Fragment>)}
        </div>
      </div>
    </div>
  );
}

function Checks({ checks }) {
  return (
    <div className="uh-tp-checks">
      {checks.map((c) => (
        <div key={c.code + c.text} className={`uh-tp-ck ${c.level}`}><Ic source={CHECK_ICON[c.level] || InfoIcon} sm /><span>{c.text}</span></div>
      ))}
    </div>
  );
}

function ChangeTable({ item }) {
  const { plan, shop } = item;
  return (
    <div className="uh-tp-chg uh-tscroll">
      <table>
        <tbody>
          {plan.metafields.map((m) => (
            <tr key={m.key}>
              <td>{m.key}</td>
              <td>{m.from === m.to
                ? <span className="uh-tp-same">{m.to} (unchanged)</span>
                : <><span className="uh-tp-from">{m.from || 'empty'}</span><span className="uh-tp-to">{m.to}</span></>}</td>
            </tr>
          ))}
          <tr><td>Vendor</td><td>{plan.vendor.from === plan.vendor.to
            ? <span className="uh-tp-same">Teamatical (unchanged)</span>
            : <><span className="uh-tp-from">{plan.vendor.from || 'blank'}</span><span className="uh-tp-to">{plan.vendor.to}</span></>}</td></tr>
          <tr><td>Status</td><td><span className="uh-tp-same">{plan.status.toLowerCase()} (never changed)</span></td></tr>
          <tr><td>Shipping profile</td><td>{plan.profile.variants_to_move
            ? <><span className="uh-tp-from">{plan.profile.from.join(', ')}</span><span className="uh-tp-to">Teamatical</span> <span className="uh-faint">· {plural(plan.profile.variants_to_move, 'variant')}</span></>
            : <span className="uh-tp-same">Teamatical (unchanged)</span>}</td></tr>
          <tr><td>Inventory</td><td>{plan.tracking_changes
            ? <>Tracked · continue selling when out of stock <span className="uh-faint">· changes {plural(plan.tracking_changes, 'variant')}</span></>
            : <span className="uh-tp-same">Tracked, continue selling (unchanged)</span>}</td></tr>
          <tr><td>Locations</td><td>
            {plan.activate.length > 0
              ? <span className="uh-tp-loc add"><span className="d" />Stock at {item.location_name} · {plural(plan.activate.length, 'variant')} at 0</span>
              : <span className="uh-tp-loc add"><span className="d" />Already stocked at {item.location_name}</span>}
            {plan.locations.map((l) => (
              <React.Fragment key={l.location_id}>
                {l.unstock.length > 0 && <span className="uh-tp-loc rm"><span className="d" />Unstock {l.location}</span>}
                {l.keep.length > 0 && <span className="uh-tp-loc keep"><span className="d" />Keep {l.location} · {l.keep_reason}</span>}
              </React.Fragment>
            ))}
            {plan.locations.filter((l) => l.negative.length).map((l) => (
              <div key={`neg-${l.location_id}`} className="uh-help">
                {l.location} shows negative stock ({l.negative.map((n) => `${n.variant} ${n.available}`).join(', ')}) with no open orders; unstocking clears it.
              </div>
            ))}
            {shop && <div className="uh-help">{plural(shop.variant_count, 'variant')} in total.</div>}
          </td></tr>
        </tbody>
      </table>
    </div>
  );
}

function ResultSteps({ result }) {
  return (
    <div className="uh-tp-res">
      {result.steps.map((s, i) => (
        <div key={i} className={s.status}>
          <Ic source={STEP_ICON[s.status] || MinusIcon} sm />
          <span>{s.label} <span className="uh-faint">· {s.detail}</span></span>
        </div>
      ))}
    </div>
  );
}

function PairRow({ item, state, onToggle, onConfirm, running }) {
  const [tone, label] = itemState(item);
  const { shop, tm } = item;
  const needsConfirm = item.confirm.length > 0 && !state.result;
  return (
    <div className={`uh-tp-pair${state.include ? '' : ' off'}`}>
      <input
        type="checkbox"
        aria-label={`Include ${shop?.title || item.shopify_url}`}
        checked={state.include}
        disabled={item.blocked || running || !!state.result}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <div className="uh-tp-body">
        <div className="uh-tp-pair-h">
          <span className="ttl">{shop?.title || item.shopify_url}</span>
          {state.result
            ? <StatusPill tone={RUN_STATE[state.result.status][0]}>{RUN_STATE[state.result.status][1]}</StatusPill>
            : state.busy ? <StatusPill tone="par">Preparing…</StatusPill> : <StatusPill tone={tone}>{label}</StatusPill>}
          {item.row && <span className="uh-help">CSV row {item.row}</span>}
          <span className="uh-sp" />
          {shop && <a className="uh-link" href={ADMIN_PRODUCT_URL + idFromGid(shop.id)} target="_blank" rel="noopener noreferrer">Open in Shopify</a>}
        </div>

        <div className="uh-tp-cmp">
          {shop ? (
            <SideCard source="Shopify · soccerpostshop" image={shop.image_url} title={shop.title} rows={[
              ['Handle', <span className="uh-mono">{shop.handle}</span>],
              ['Status', shop.status.toLowerCase()],
              ['SKU', <span className="uh-mono">{shop.skus.length > 3 ? `${shop.skus[0]} … ${shop.skus[shop.skus.length - 1]}` : shop.skus.join(', ') || '—'}</span>],
              ['Variants', shop.variant_count],
              ['Current ID', shop.teamatical_id ? <code className="uh-tp-code">{shop.teamatical_id}</code> : <span className="uh-faint">none</span>],
            ]} />
          ) : <SideCard source="Shopify" title="Not found" rows={[['Link', <span className="uh-mono">{item.shopify_url}</span>]]} />}
          <Checks checks={item.checks} />
          {tm ? (
            <SideCard source="Teamatical · front" image={<TeamaticalImage teamaticalId={tm.id} />} title={tm.title} rows={[
              ['ID', <code className="uh-tp-code">{tm.id}</code>],
              tm.store_title && ['Store', tm.store_title],
              ['SKU', <span className="uh-mono">{tm.sku || '—'}</span>],
              tm.sizes && ['Sizes', tm.sizes.join(', ')],
              tm.price_cents != null && ['Price', `$${(tm.price_cents / 100).toFixed(2)}`],
            ].filter(Boolean)} />
          ) : <SideCard source="Teamatical" title="Not found" rows={[['Link', <span className="uh-mono">{item.teamatical_url}</span>]]} />}
        </div>

        {state.result ? <ResultSteps result={state.result} />
          : state.error ? <Alert tone="crit">{state.error}</Alert>
          : item.plan && <ChangeTable item={item} />}

        {needsConfirm && item.confirm.map((reason) => (
          <label key={reason} className={`uh-tp-override${reason === 'sku_mismatch' ? ' bad' : ''}`}>
            <input type="checkbox" checked={state.confirmed.includes(reason)} disabled={running}
              onChange={(e) => onConfirm(reason, e.target.checked)} />
            <span><b>{CONFIRM_TEXT[reason](item)}</b>. Confirm the two images show the same product and fit.</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// How the product looked before the first Prepare, for undoing by hand.
function BeforeState({ before }) {
  if (!before || !before.variants) {
    return <div className="uh-help">Before Prepare: not recorded (prepared before snapshots were added on Sep 25).</div>;
  }
  const list = (o) => Object.entries(o || {}).map(([k, n]) => `${k} (${n})`).join(', ') || 'none';
  const mf = Object.entries(before.metafields || {}).filter(([, v]) => v).map(([k, v]) => `${k} = ${v}`).join(', ') || 'none';
  return (
    <div className="uh-help">
      <b>Before Prepare:</b> vendor {before.vendor || 'blank'} · Teamatical_v1 {mf} · shipping profile {list(before.profiles)} ·
      {` ${before.tracked} of ${before.variants} tracked, ${before.continue_selling} continue selling`} · stocked at {list(before.locations)}
    </div>
  );
}

function HistoryTable({ rows, limit, openId, onToggle, onRetry, retrying, loading, error }) {
  const { nameOf } = useAuth();
  if (loading) return <div className="uh-empty">Loading history…</div>;
  if (error) return <div className="uh-card-b"><Alert tone="crit">{error}</Alert></div>;
  if (!rows.length) return <div className="uh-empty">No products prepared yet.</div>;
  const shown = limit ? rows.slice(0, limit) : rows;
  return (
    <div className="uh-tscroll">
      <table className="uh-tbl">
        <thead><tr><th>Product</th><th>Teamatical ID</th><th className="r">Variants</th><th>Other locations</th><th>Prepared by</th><th>When</th><th>Status</th><th /></tr></thead>
        <tbody>
          {shown.map((r) => {
            const open = openId === r.id;
            const [tone, label] = RUN_STATE[r.status] || ['neu', r.status];
            const pips = [
              ...(r.unstocked_locations || []).map((l) => ['ful', l]),
              ...(r.kept_locations || []).map((k) => ['hold', k.location]),
              ...(r.status === 'failed' ? [['can', 'failed']] : []),
            ];
            return (
              <React.Fragment key={r.id}>
                <tr className="click" onClick={() => onToggle(open ? null : r.id)}>
                  <td><div className="nm">{r.title}</div><div className="sm uh-mono">{r.handle}</div></td>
                  <td>
                    <code className="uh-tp-code">{r.teamatical_id}</code>
                    {r.previous_teamatical_id && r.previous_teamatical_id !== r.teamatical_id && <div className="sm">was {r.previous_teamatical_id}</div>}
                  </td>
                  <td className="r uh-num">{r.variant_count}</td>
                  <td><span className="uh-pips">{pips.length ? pips.map(([t, l], i) => <i key={i} className={`uh-pip ${t}`} title={l} />) : <EmDash />}</span></td>
                  <td>{nameOf(r.prepared_by)}</td>
                  <td className="uh-num">{formatDateTime(r.updated_at || r.created_at)}</td>
                  <td><StatusPill tone={tone}>{label}</StatusPill></td>
                  <td className="go"><Ic source={open ? ChevronDownIcon : ChevronRightIcon} sm /></td>
                </tr>
                {open && (
                  <tr className="uh-x"><td colSpan={8}>
                    <div className="uh-col" style={{ gap: 8 }}>
                      <ResultSteps result={r} />
                      {r.status === 'pending_cleanup' && (
                        <div className="uh-help">
                          Nightly retry: {r.last_auto_retry_at ? `last checked ${formatDateTime(r.last_auto_retry_at)}` : 'runs tonight at 3 AM Eastern'}. Unstocks automatically once the orders ship.
                        </div>
                      )}
                      <BeforeState before={r.before_state} />
                      <div className="uh-actions">
                        {(r.status === 'pending_cleanup' || r.status === 'failed') && (
                          <button type="button" className={`uh-btn slim${retrying === r.id ? ' busy' : ''}`} disabled={retrying === r.id} onClick={() => onRetry(r)}>
                            <Ic source={RefreshIcon} sm />{retrying === r.id ? 'Retrying…' : r.status === 'failed' ? 'Retry failed steps' : 'Retry cleanup'}
                          </button>
                        )}
                        {r.status === 'pending_cleanup' && <span className="uh-help">Checks the open orders again and unstocks every location it can.</span>}
                        <span className="uh-sp" />
                        <a className="uh-link" href={ADMIN_PRODUCT_URL + idFromGid(r.shopify_product_id)} target="_blank" rel="noopener noreferrer">Open in Shopify</a>
                        <a className="uh-link" href={r.teamatical_url} target="_blank" rel="noopener noreferrer">Open in Teamatical</a>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function TeamaticalPrep() {
  const [params, setParams] = useSearchParams();
  const initialTab = TABS.includes(params.get('tab')) ? params.get('tab') : 'single';
  const [tab, setTabState] = useState(initialTab);
  const { refresh: refreshAlerts } = useAlerts();

  const [shopUrl, setShopUrl] = useState('');
  const [tmUrl, setTmUrl] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const csvInputRef = useRef(null);

  const [items, setItems] = useState(null);
  const [states, setStates] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const previewRef = useRef(null);

  const [runs, setRuns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState('');
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditBusy, setAuditBusy] = useState(null);

  const setTab = (t) => {
    setTabState(t);
    setParams(t === 'single' ? {} : { tab: t }, { replace: true });
  };
  const switchStartTab = (t) => { setItems(null); setPreviewError(''); setPreviewErrors([]); setTab(t); };

  const loadHistory = useCallback(async () => {
    try {
      setRuns((await api.history()).runs || []);
      setHistoryError('');
    } catch (e) {
      setHistoryError(`Couldn't load history: ${e.message}`);
    } finally {
      setHistoryLoading(false);
    }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadAudit = useCallback(async () => {
    try {
      setAuditData(await api.audit());
      setAuditError('');
    } catch (e) {
      setAuditError(`Couldn't load the audit: ${e.message}`);
    } finally {
      setAuditLoading(false);
    }
  }, []);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const auditAction = async (row, fn, label) => {
    setAuditBusy(row.product_id);
    try {
      setAuditData(await fn());
      setAuditError('');
      refreshAlerts();
    } catch (e) {
      setAuditError(`${label} failed: ${e.message}`);
    } finally {
      setAuditBusy(null);
    }
  };
  const runAudit = async () => {
    setAuditRunning(true);
    try {
      setAuditData(await api.runAudit());
      setAuditError('');
      refreshAlerts();
    } catch (e) {
      setAuditError(e.message);
    } finally {
      setAuditRunning(false);
    }
  };
  // Prepare is idempotent, so "fix" means preview this product again.
  const fixWithPrepare = (row) => {
    setShopUrl(ADMIN_PRODUCT_URL + idFromGid(row.product_id));
    setTmUrl(row.run?.teamatical_url || `https://soccerpost.teamatical.com/detail/--/${row.teamatical_id}`);
    switchStartTab('single');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const auditRetry = (row) => auditAction(row, async () => { await api.retry(row.run.id); loadHistory(); return api.runAudit(); }, 'Retry');

  const stats = useMemo(() => {
    const since = Date.now() - STATS_WINDOW_DAYS * 86400 * 1000;
    const recent = runs.filter((r) => new Date(r.created_at).getTime() >= since);
    const count = (s) => runs.filter((r) => r.status === s).length;
    return { total: recent.length, complete: count('complete'), pending: count('pending_cleanup'), failed: count('failed') };
  }, [runs]);

  const filteredRuns = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    return runs.filter((r) => (statusFilter === 'all' || r.status === statusFilter)
      && (!q || `${r.title} ${r.handle} ${r.teamatical_id} ${r.previous_teamatical_id || ''}`.toLowerCase().includes(q)));
  }, [runs, historySearch, statusFilter]);

  const showPreview = (data) => {
    setItems(data.items);
    setPreviewErrors(data.errors || []);
    // Clean rows start ticked; anything blocked or needing a confirmation starts unticked.
    setStates(data.items.map((it) => ({ include: !it.blocked && it.confirm.length === 0, confirmed: [], busy: false, result: null, error: '' })));
    setTimeout(() => previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      showPreview(tab === 'single'
        ? await api.preview([{ shopify_url: shopUrl.trim(), teamatical_url: tmUrl.trim() }])
        : await api.csvPreview(csvFile));
    } catch (e) {
      setItems(null);
      setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const updateState = (i, patch) => setStates((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const selected = items ? items.map((it, i) => [it, states[i]]).filter(([it, s]) => s?.include && !it.blocked && !s.result) : [];
  const unconfirmed = selected.filter(([it, s]) => it.confirm.some((r) => !s.confirmed.includes(r)));
  const finished = items ? states.filter((s) => s.result).length : 0;
  const canRun = !running && selected.length > 0 && unconfirmed.length === 0;

  // One request per product, in order, so each row shows its own progress.
  const runPrepare = async () => {
    setRunning(true);
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      const s = states[i];
      if (!s.include || it.blocked || s.result) continue;
      updateState(i, { busy: true, error: '' });
      try {
        const result = await api.prepare(it, s.confirmed);
        updateState(i, { busy: false, result });
      } catch (e) {
        updateState(i, { busy: false, error: e.message });
      }
    }
    setRunning(false);
    loadHistory();
    refreshAlerts();
  };

  const retry = async (run) => {
    setRetrying(run.id);
    try {
      const updated = await api.retry(run.id);
      setRuns((prev) => prev.map((r) => (r.id === run.id ? updated : r)));
      refreshAlerts();
    } catch (e) {
      setHistoryError(`Retry failed: ${e.message}`);
    } finally {
      setRetrying(null);
    }
  };

  const statButton = (key, value, label) => (
    <button type="button" className="uh-stat" aria-pressed={key !== 'all' && statusFilter === key}
      onClick={() => { setStatusFilter(key === 'all' || statusFilter === key ? 'all' : key); setTab('history'); }}>
      <span className="v">{value}</span>
      <span className="k">{label}</span>
    </button>
  );

  const historyControls = (
    <>
      <span className="uh-sp" />
      <div className="uh-search">
        <Ic source={SearchIcon} sm />
        <input value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} placeholder="Search title, handle or Teamatical ID" aria-label="Search history" />
      </div>
      <select className="uh-select" aria-label="Status filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        {FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </>
  );

  const attention = stats.pending + stats.failed;
  const auditAttention = auditData?.audit?.counts?.attention || 0;
  const onStartTab = tab === 'single' || tab === 'csv';
  const tmId = teamaticalIdFromUrl(tmUrl);

  const history = (limit) => (
    <HistoryTable rows={filteredRuns} limit={limit} openId={openId} onToggle={setOpenId}
      onRetry={retry} retrying={retrying} loading={historyLoading} error={historyError} />
  );
  const legend = (
    <span className="uh-legend">
      <span><i className="uh-pip ful" />Unstocked</span>
      <span><i className="uh-pip hold" />Kept (open orders)</span>
      <span><i className="uh-pip can" />Failed</span>
    </span>
  );

  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div className="uh-tp-title">
          <span className="uh-tp-mark"><Ic source={TeamaticalIcon} /></span>
          <div>
            <h1 className="uh-h1">Teamatical Prep</h1>
            <div className="uh-sub">Link soccerpostshop products to Teamatical so orders drop-ship from Teamatical’s location.</div>
          </div>
        </div>
        <div className="uh-statcard">
          <div className={`state${attention ? ' hold' : ''}`}>
            <span className="ic"><Ic source={attention ? AlertTriangleIcon : CheckIcon} sm /></span>
            <div>
              <b>Prep status</b>
              <span className="line">
                {attention
                  ? [stats.pending && `${stats.pending} waiting on open orders`, stats.failed && `${stats.failed} failed`].filter(Boolean).join(' · ')
                  : 'Everything prepared'}
              </span>
            </div>
          </div>
          <div className="stats">
            {statButton('all', stats.total, `Prepared (${STATS_WINDOW_DAYS}d)`)}
            {statButton('complete', stats.complete, 'Complete')}
            {statButton('pending_cleanup', stats.pending, 'Pending cleanup')}
            {statButton('failed', stats.failed, 'Failed')}
          </div>
        </div>
      </div>

      <div className="uh-card">
        <div className="uh-tabs" role="tablist" aria-label="Teamatical prep">
          {[['single', TeamaticalIcon, 'Single product'], ['csv', FileIcon, 'CSV import'], ['history', ClockIcon, 'History'], ['audit', SearchIcon, 'Audit']].map(([key, icon, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
              onClick={() => (key === 'history' ? setTab(key) : switchStartTab(key))}>
              <Ic source={icon} sm />{label}
              {key === 'history' && runs.length > 0 && <span className={`uh-tcount${attention ? ' warn' : ''}`}>{runs.length}</span>}
              {key === 'audit' && auditAttention > 0 && <span className="uh-tcount warn">{auditAttention}</span>}
            </button>
          ))}
        </div>
      </div>

      {onStartTab && (
        <>
          <div className="uh-start">
            <div className="uh-card uh-card-b uh-form-card">
              <div className="uh-note">
                <Ic source={InfoIcon} sm />
                <div>{tab === 'single'
                  ? 'Paste the product’s Shopify link (admin or storefront) and its Teamatical link. Nothing changes until you compare both products side by side and click Prepare.'
                  : 'One product per row. Every row is looked up and shown side by side; untick any you don’t want before clicking Prepare.'}</div>
              </div>

              {tab === 'single' ? (
                <div className="uh-grid2">
                  <div className="uh-fld">
                    <label htmlFor="tp-shop">Shopify product URL <span className="req">*</span></label>
                    <input id="tp-shop" className="uh-inp" value={shopUrl} onChange={(e) => setShopUrl(e.target.value)}
                      placeholder="https://admin.shopify.com/store/soccerpostshop/products/…" autoComplete="off" />
                    <span className="uh-help">The product’s admin link or its storefront link (…/products/&lt;handle&gt;). Unlisted products work too.</span>
                  </div>
                  <div className="uh-fld">
                    <label htmlFor="tp-tm">Teamatical product URL <span className="req">*</span></label>
                    <input id="tp-tm" className="uh-inp" value={tmUrl} onChange={(e) => setTmUrl(e.target.value)}
                      placeholder="https://soccerpost.teamatical.com/detail/store-…/…" autoComplete="off" />
                    <span className="uh-tp-idchip">Teamatical ID <code>{tmId || '—'}</code></span>
                  </div>
                </div>
              ) : (
                <div className="uh-fld">
                  <label htmlFor="tp-csv">Products CSV</label>
                  <div className="uh-actions">
                    <input id="tp-csv" ref={csvInputRef} type="file" accept=".csv" hidden onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                    <button type="button" className="uh-btn" onClick={() => csvInputRef.current?.click()}>
                      <Ic source={UploadIcon} sm />{csvFile ? 'Choose a different file' : 'Choose CSV file'}
                    </button>
                    {csvFile ? <span className="uh-mono">{csvFile.name}</span> : <span className="uh-help">No file chosen</span>}
                  </div>
                  <span className="uh-help">Header row with <code>Shopify Product URL</code> (admin or storefront link) and <code>Teamatical Product URL</code> columns, any order. Up to 100 rows.</span>
                </div>
              )}

              {previewError && <Alert tone="crit">{previewError}</Alert>}
              <button type="button" className={`uh-btn primary wide${previewLoading ? ' busy' : ''}`}
                disabled={previewLoading || running || (tab === 'single' ? !shopUrl.trim() || !tmUrl.trim() : !csvFile)}
                onClick={runPreview}>
                <Ic source={ViewIcon} sm />{previewLoading ? 'Looking up products…' : 'Preview'}
              </button>
            </div>

            <div className="uh-card uh-card-b">
              <h2 className="uh-h2 lg">What happens next</h2>
              <ol className="uh-steps">
                <li><span className="sn">1</span><div><b>Look up both products</b><span>Shopify title, image, SKUs and stock by location; Teamatical title, front image, SKU and store.</span></div></li>
                <li><span className="sn">2</span><div><b>You compare them side by side</b><span>Flags a SKU or fit mismatch (Men’s, Women’s, Youth), an existing or different Teamatical ID, or an ID already on another product.</span></div></li>
                <li><span className="sn">3</span><div><b>Set metafields and vendor</b><span>Teamatical_v1 teamaticalID, playerNumber = Number, playerName = Name. Vendor = Teamatical. Status is never changed.</span></div></li>
                <li><span className="sn">4</span><div><b>Stock at the Teamatical location</b><span>Every variant: tracked, continue selling at 0, quantity 0.</span></div></li>
                <li><span className="sn">5</span><div><b>Move to the Teamatical shipping profile</b><span>All variants leave their current profile.</span></div></li>
                <li><span className="sn">6</span><div><b>Unstock every other location</b><span>Locations with open orders stay stocked (Shopify blocks it) and show as pending cleanup until those orders ship.</span></div></li>
              </ol>
            </div>
          </div>

          {items && (
            <div className="uh-card" ref={previewRef}>
              <div className="uh-card-h">
                <h2 className="uh-h2">{items.length === 1 ? `Preview · ${items[0].shop?.title || 'product'}` : `Preview · ${plural(items.length, 'product')}`}</h2>
                {finished ? <StatusPill tone="ful">{plural(finished, 'product')} prepared</StatusPill> : <StatusPill tone="neu">Nothing has changed yet</StatusPill>}
                <span className="uh-sp" />
                <span className="uh-help uh-num">
                  {[
                    ['ful', 'ready'], ['neu', 'already configured'], ['hold', 'to review'], ['can', 'to check'],
                  ].map(([t, l]) => [items.filter((it) => itemState(it)[0] === t).length, l]).filter(([n]) => n).map(([n, l]) => `${n} ${l}`).join(' · ')}
                </span>
              </div>
              {previewErrors.length > 0 && (
                <div className="uh-card-b" style={{ paddingBottom: 0 }}>
                  <Alert><b>{plural(previewErrors.length, 'row')} skipped.</b>{previewErrors.map((e) => <div key={e}>{e}</div>)}</Alert>
                </div>
              )}
              <div>
                {items.map((it, i) => (
                  <PairRow key={`${it.shopify_url}|${it.teamatical_url}|${i}`} item={it} state={states[i]} running={running}
                    onToggle={(v) => updateState(i, { include: v })}
                    onConfirm={(reason, v) => updateState(i, {
                      confirmed: v ? [...states[i].confirmed, reason] : states[i].confirmed.filter((r) => r !== reason),
                      include: v ? true : states[i].include,
                    })} />
                ))}
              </div>
              <div className="uh-confirm" style={{ alignItems: 'center' }}>
                <span className="uh-help">
                  {running ? 'Preparing…'
                    : !selected.length ? (finished ? 'Done. Results are above and in History.' : 'Tick at least one product.')
                    : unconfirmed.length ? `${plural(unconfirmed.length, 'selected product')} need${unconfirmed.length === 1 ? 's' : ''} the confirmation ticked, or untick ${unconfirmed.length === 1 ? 'it' : 'them'}.`
                    : 'Writes to soccerpostshop. Every change is listed above; undo by hand in Shopify if needed.'}
                </span>
                <span className="uh-sp" />
                <button type="button" className="uh-btn" disabled={running} onClick={() => setItems(null)}>{finished ? 'Close' : 'Cancel'}</button>
                <button type="button" className={`uh-btn primary${running ? ' busy' : ''}`} disabled={!canRun} onClick={runPrepare}>
                  <Ic source={PlayIcon} sm />{running ? 'Preparing…' : `Prepare ${plural(selected.length, 'product')}`}
                </button>
              </div>
            </div>
          )}

          <div className="uh-card">
            <div className="uh-card-h">
              <div><h2 className="uh-h2 lg">History</h2><div className="uh-sub">Every product prepared with this tool, newest first.</div></div>
              {historyControls}
            </div>
            {history(RECENT_ROWS)}
            <div className="uh-tfoot">
              {legend}
              {filteredRuns.length > RECENT_ROWS && (
                <span className="uh-pager"><button type="button" className="uh-link" onClick={() => setTab('history')}>View all {filteredRuns.length}</button></span>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <AuditPanel
          data={auditData} loading={auditLoading} error={auditError} running={auditRunning} busyId={auditBusy}
          onRun={runAudit}
          onIgnore={(r) => auditAction(r, () => api.ignore(r, 'Ignored from the Audit tab'), 'Ignore')}
          onUnignore={(r) => auditAction(r, () => api.unignore(r), 'Stop ignoring')}
          onFix={fixWithPrepare}
          onRetry={auditRetry}
        />
      )}

      {tab === 'history' && (
        <div className="uh-card">
          <div className="uh-card-h">
            <div><h2 className="uh-h2 lg">History</h2><div className="uh-sub">Click a row for each step’s result. Retry picks up only what’s left.</div></div>
            {historyControls}
          </div>
          {history(0)}
          <div className="uh-tfoot">{legend}<span className="uh-pager uh-num">{plural(filteredRuns.length, 'product')}</span></div>
        </div>
      )}
    </div>
  );
}
